import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Card,
  Collapse,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  Pagination,
  TablePagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Dbutton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import ThreeWayMatchTable from "./purchaseorder";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import { format } from "date-fns";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TWMOverviewDashboard from "./TWMOverviewDashboard";

// Status Chip Component for main table
const StatusChip = ({ status, type }) => {
  const config = {
    matched: { color: "#2E7D32", bgcolor: "#E8F5E9", icon: "✓" },
    partial: { color: "#ED6C02", bgcolor: "#FFF3E0", icon: "⚠" },
    rejected: { color: "#D32F2F", bgcolor: "#FFEBEE", icon: "✗" },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, icon } = config[type] || config.default;

  return (
    <Chip
      label={`${icon} ${status}`}
      size="small"
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        height: "24px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${color}20`,
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
};

// Helper to analyze quantity match status
const analyzeQuantityMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || !grnItems.length || !invoiceItems.length) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    // Check each PO item
    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poQty = parseFloat(poItem.qty || poItem.parsed_qty || 0);

      // Find matching GRN item
      const grnItem = grnItems.find(
        (item) => item.part_no === poPartNo || item.item_code === poPartNo
      );

      // Find matching Invoice item
      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo)
      );

      const grnQty = parseFloat(grnItem?.received_q || grnItem?.qty || 0);
      const invQty = parseFloat(
        invoiceItem?.qty || invoiceItem?.parsed_qty || 0
      );

      // If item not found in GRN or Invoice → PARTIAL (not rejected)
      if (!grnItem || !invoiceItem) {
        partial++;
      }
      // ✅ MATCHED: Exact quantity match across all three
      else if (poQty === grnQty && poQty === invQty) {
        matched++;
      }
      // 🔴 REJECTED: Quantity EXCEEDING in GRN or Invoice (greater than)
      else if (grnQty > poQty || invQty > poQty) {
        rejected++;
      }
      // 🟡 PARTIAL: Quantity LESS in GRN or Invoice (smaller than)
      else if (grnQty < poQty || invQty < poQty) {
        partial++;
      } else {
        matched++;
      }

      totalChecked++;
    });

    // Determine overall status
    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze price match status
const analyzePriceMatch = (poData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || !invoiceItems.length) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poPrice = parseFloat(poItem.rate || poItem.parsed_rate || 0);

      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo)
      );

      // If item not found → PARTIAL (not rejected)
      if (!invoiceItem) {
        partial++;
      } else {
        const invPrice = parseFloat(
          invoiceItem.rate || invoiceItem.parsed_rate || 0
        );

        // 🔴 REJECTED: Price INCREASED in invoice (greater than)
        if (invPrice > poPrice) {
          rejected++;
        }
        // 🟡 PARTIAL: Price DECREASED in invoice (smaller than)
        else if (invPrice < poPrice) {
          partial++;
        }
        // ✅ MATCHED: Same price
        else {
          matched++;
        }
      }

      totalChecked++;
    });

    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze item match status
const analyzeItemMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length) {
      return { status: "No PO Items", type: "rejected" };
    }

    const poItemCount = poItems.length;
    const grnItemCount = grnItems.length;
    const invoiceItemCount = invoiceItems.length;

    // 🔴 REJECTED: GRN has MORE items than PO (EXCEEDING)
    if (grnItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // 🔴 REJECTED: Invoice has MORE items than PO (EXCEEDING)
    if (invoiceItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // 🟡 PARTIAL: GRN has LESS items than PO
    if (grnItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // 🟡 PARTIAL: Invoice has LESS items than PO
    if (invoiceItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // ✅ MATCHED: All item counts match
    if (poItemCount === grnItemCount && poItemCount === invoiceItemCount) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

const getMatchStatus = (result_json) => {
  try {
    const poItems = result_json?.po_data || [];
    const grnItems = result_json?.grn_data || [];
    const invoiceItems = result_json?.invoice_data || [];

    const normalize = (v) => String(v).trim().toLowerCase();

    let allMatched = true;

    poItems.forEach((poItem) => {
      const code = normalize(poItem?.part_no || poItem?.item_code);

      const grn = grnItems.find(
        (g) => normalize(g?.part_no || g?.item_code) === code
      );
      const inv = invoiceItems.find(
        (i) => normalize(i?.part_no || i?.item_code) === code
      );

      const poQty = parseFloat(poItem?.qty || 0);
      const grnQty = parseFloat(grn?.qty || 0);
      const invQty = parseFloat(inv?.qty || 0);

      if (
        isNaN(poQty) ||
        isNaN(grnQty) ||
        isNaN(invQty) ||
        poQty !== grnQty ||
        poQty !== invQty
      ) {
        allMatched = false;
      }
    });

    return allMatched ? "Matched" : "Partially Matched";
  } catch {
    return "Partially Matched";
  }
};

const DownloadMenuButton = ({ onCSV }) => {
  return (
    <IconButton onClick={onCSV} sx={{ p: 0.5 }}>
      <DownloadIcon />
    </IconButton>
  );
};

const EmptyState = ({ title = "No records found", subtitle }) => (
  <Box sx={{ textAlign: "center", py: 8, px: 2, color: "text.secondary" }}>
    <AssignmentTurnedInIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Date Format
const formatDate = (date) => {
  if (!date) return "—";

  let day, month, year;

  // DD/MM/YYYY or DD-MM-YYYY
  if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(date)) {
    const parts = date.includes("/") ? date.split("/") : date.split("-");
    [day, month, year] = parts.map(Number);
  }
  // YYYY-M-D or YYYY-MM-DD
  else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
    [year, month, day] = date.split("-").map(Number);
  }
  // Try JS Date fallback
  else {
    const parsed = new Date(date);
    if (isNaN(parsed)) return "—";
    day = parsed.getDate();
    month = parsed.getMonth() + 1;
    year = parsed.getFullYear();
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthName = monthNames[month - 1];
  const dayStr = day.toString().padStart(2, "0");

  return `${dayStr}-${monthName}-${year}`;
};

// Updated ApprovalStatusTooltip component
const ApprovalStatusTooltip = ({ approvers }) => {
  if (!approvers || approvers.length === 0) return null;

  const approved = approvers.filter((a) => a.status === "APPROVED");
  const pending = approvers.filter((a) => a.status === "PENDING");
  const rejected = approvers.filter((a) => a.status === "REJECTED");

  return (
    <Box sx={{ p: 1.5, minWidth: 220 }}>
      {/* ✅ Approved */}
      {approved.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#0bf063ff",
              fontSize: "13px",
            }}
          >
            ✅ Approved ({approved.length})
          </Typography>
          {approved.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#f0fdf4",
                mb: 0.5,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: "#166534", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#308350ff", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ⏳ Pending */}
      {pending.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#e98b1fff",
              fontSize: "13px",
            }}
          >
            ⏳ Pending ({pending.length})
          </Typography>
          {pending.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fefce8",
                mb: 0.5,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 16, color: "#854d0e", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#713f12", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ❌ Rejected */}
      {rejected.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#991b1b", fontSize: "13px" }}
          >
            ❌ Rejected ({rejected.length})
          </Typography>
          {rejected.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fef2f2",
                mb: 0.5,
              }}
            >
              <CancelIcon sx={{ fontSize: 16, color: "#991b1b", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#7f1d1d", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ThreeWayMatching = ({ uploadmode, onModeChange }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(loginData?.auth_token);
  const [user_id] = useState(loginData?.user_id);

  const [user_token] = useState(loginData?.user?.user_token);
  const [user_designation] = useState(loginData?.user?.designation);
  const user_role = loginData?.user?.role;

  // UI states
  const [mode, setMode] = useState(uploadmode);
  const [searchText, setSearchText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [response, setResponse] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [overallProgress, setOverallProgress] = useState(0);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const [isDragging, setIsDragging] = useState(false);

  const [getmatchdata, setMatchedData] = useState(null);
  const [matchedresponse, setMatchedResponse] = useState(null);
  const [viewData, setViewData] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [savedTwmCode, setSavedTwmCode] = useState("");
  const [approvedTokens, setApprovedTokens] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewItemsDialog, setViewItemsDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const [nextTwmCode, setNextTwmCode] = useState("");

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortByInvoiceRef = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
  };

  // Sort and paginate data
  const getSortedData = () => {
    return [...filterRecords].sort((a, b) => {
      const aCode = a.match_code || "";
      const bCode = b.match_code || "";

      if (sortOrder === "desc") {
        return bCode.localeCompare(aCode);
      } else {
        return aCode.localeCompare(bCode);
      }
    });
  };

  const getCurrentPageData = () => {
    const sortedData = getSortedData();
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  };

  // Refs for upload
  const controllerRef = useRef(null);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchNextCode = async () => {
    try {
      const { data } = await axios.post(
        API_ENDPOINT.peek_next_twm_code,
        { user_token },
        { headers: { Authorization: `Bearer ${auth_token}` } }
      );
      setNextTwmCode(data?.next_match_code || "");
    } catch {
      setNextTwmCode("");
    }
  };

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, []);

  const scrollByAmount = (dx) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  useEffect(() => {
    if (uploadmode && ["main", "upload", "view"].includes(uploadmode)) {
      setMode(uploadmode);
    }
  }, [uploadmode]);

  const go = (next) => {
    if (typeof onModeChange === "function") onModeChange(next);
    else setMode(next);
  };

  useEffect(() => {
    if (mode !== "upload" || !dropRef.current) return;
    const el = dropRef.current;
    const onDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);
    const onDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) {
        setSelectedFiles((prev) => [...prev, ...files]);
        setResponse(null);
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [mode]);

  // File upload handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    setResponse(null);
  };
  const handleRemoveFile = (fileName) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleCancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      setUploadStatus("idle");
      setOverallProgress(0);
      setSnack({ open: true, type: "info", msg: "Upload cancelled." });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || uploadStatus === "uploading") return;
    setUploadStatus("uploading");
    setOverallProgress(0);
    setResponse(null);

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));

    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const res = await axios.post(API_ENDPOINT.extractdoc, formData, {
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setOverallProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setResponse(res?.data || {});
      setSnack({
        open: true,
        type: "success",
        msg: "Files uploaded & extracted successfully.",
      });
      setUploadStatus("success");
      setOverallProgress(100);
    } catch (err) {
      if (axios.isCancel?.(err)) return;
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Upload failed. Please try again.",
      });
      setUploadStatus("error");
    } finally {
      controllerRef.current = null;
    }
  };
  console.log(matchedresponse);
  // Search handlers
  const filterRecords = (matchedresponse || []).filter(
    (item) =>
      item.match_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.result_json?.grn_data?.some((grn) =>
        grn.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      item.result_json?.po_data?.some((poNO) =>
        poNO.purchase_order_no.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      item.result_json?.invoice_data?.some((invoiceNO) =>
        invoiceNO.invoice_number
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleClear = () => {
    setSearchText("");
  };

  // Upload status chip
  const statusChip = useMemo(() => {
    switch (uploadStatus) {
      case "uploading":
        return (
          <Chip
            label={`Uploading ${overallProgress}%`}
            color="info"
            size="small"
            sx={{ fontSize: "12px" }}
          />
        );
      case "success":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Success"
            color="success"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      case "error":
        return (
          <Chip
            label="Error"
            color="error"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      default:
        return (
          <Chip
            label="Waiting to upload"
            variant="outlined"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
    }
  }, [uploadStatus, overallProgress]);

  // File size display
  const formatKB = (bytes) => `${(bytes / 1024).toFixed(2)} KB`;

  const handleSaveMatching = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please upload at least one file before saving.",
      });
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("file", file));
    formData.append("user_token", user_token);
    formData.append("match_code", nextTwmCode || "");
    formData.append(
      "result_json",
      typeof response === "string" ? response : JSON.stringify(response || {})
    );

    try {
      const request = await axios({
        url: `${API_ENDPOINT.save_three_way_matching}`,
        data: formData,
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSavedTwmCode(nextTwmCode);
      setShowSuccessDialog(true);
      go("main");
      await fetchMatchedData();
      setSnack({
        open: true,
        type: "success",
        msg: "Matching saved successfully!",
      });

      setSelectedFiles([]);
      setResponse(null);
      setUploadStatus("idle");
      setOverallProgress(0);
    } catch (err) {
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Save failed. Please try again.",
      });
    }
  };
  const handleSaveMatchingWithLoader = async () => {
    setSaving(true);
    try {
      await handleSaveMatching(); // your original save logic
    } finally {
      setSaving(false);
    }
  };

  const fetchMatchedData = async () => {
    try {
      const res = await axios.post(
        `${API_ENDPOINT.get_three_way_matching}`,
        { user_token },
        { headers: { Authorization: `Bearer ${auth_token}` } }
      );

      const twmList = res?.data || [];
      setMatchedResponse(twmList);

      // Set latest one
      const latest = twmList?.[0]?.result_json || null;
      setMatchedData(latest);

      const enrichedWithApprovers = await Promise.all(
        twmList.map(async (twm) => {
          try {
            const approverRes = await axios.post(
              `${API_ENDPOINT.get_twm_approver_names}`,
              { twm_token: twm.token },
              { headers: { Authorization: `Bearer ${auth_token}` } }
            );

            return {
              ...twm,
              approvers: approverRes.data?.approvers || [],
            };
          } catch (err) {
            console.error("Failed to fetch approvers for TWM:", twm.token);
            return {
              ...twm,
              approvers: [],
            };
          }
        })
      );

      // Optional: set this enriched list to another state
      setMatchedResponse(enrichedWithApprovers);
    } catch (error) {
      console.error("Error fetching TWM data", error);
    }
  };

  useEffect(() => {
    fetchMatchedData();
  }, []);

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action, three_way_search } = JSON.parse(chatActionRaw);
    console.log(three_way_search);

    if (action === "export_data") {
      if (!matchedresponse || matchedresponse.length === 0) {
        // wait until data is loaded
        return;
      }

      sessionStorage.removeItem("CHAT_ACTION");
      downloadAllCSV();
    }

    if (action === "search_records") {
      setSearchTerm(three_way_search);
    }

    if (action === "create_twm") {
      fetchNextCode();
      go("upload");
    }
  }, [matchedresponse]);

  const buildViewDataFromMatches = (matchesObj) => {
    if (!matchesObj || typeof matchesObj !== "object") return null;
    return {
      poData: Array.isArray(matchesObj.po_data) ? matchesObj.po_data : [],
      invoiceData: Array.isArray(matchesObj.invoice_data)
        ? matchesObj.invoice_data
        : [],
      grnData: Array.isArray(matchesObj.grn_data) ? matchesObj.grn_data : [],
    };
  };

  const toggleReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  };

  const closeReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: false }));
  };

  const approveAndRejectDoc = async (action, twm_token) => {
    const formData = new FormData();

    formData.append("action", action);
    formData.append("user_token", user_token);
    formData.append("twm_token", twm_token);

    const request = await axios({
      url: `${API_ENDPOINT.approve_reject_twm}`,
      data: formData,
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth_token}`,
      },
    });

    if (action === "accept") {
      setApprovedTokens((prev) => [...prev, twm_token]);
    }

    await fetchMatchedData();
  };

  const buildRowsFromResponse = (dataObj = {}) => {
    const rows = [];

    // ---- PO ----
    const poList = Array.isArray(dataObj.po_data) ? dataObj.po_data : [];
    poList.forEach((po) => {
      const items = Array.isArray(po.line_items) ? po.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "PO",
          doc_no: po.purchase_order_no || po.order_number || "",
          doc_date: po.order_date || "",
          vendor: po.vendor || "",
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li["qty."] != null
              ? li["qty."]
              : li.qty != null
              ? li.qty
              : li.received_q != null
              ? li.received_q
              : "",
          uom: li.uom || "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
              ? li.unit_price
              : "",
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
              ? li.line_total
              : li.parsed_amount != null
              ? li.parsed_amount
              : "",
        });
      });
    });

    // ---- GRN ----
    const grnList = Array.isArray(dataObj.grn_data) ? dataObj.grn_data : [];
    grnList.forEach((grn) => {
      const items = Array.isArray(grn.line_items) ? grn.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "GRN",
          doc_no: grn.order_number || "",
          doc_date: grn.date || "",
          vendor: grn.vendor_name || "",
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.received_q != null
              ? li.received_q
              : li["qty."] != null
              ? li["qty."]
              : li.qty != null
              ? li.qty
              : "",
          uom: li.uom || "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
              ? li.unit_price
              : "",
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
              ? li.line_total
              : li.parsed_amount != null
              ? li.parsed_amount
              : "",
          lot_number: li.lot_number || "",
          expiry_date: li.expiry_date || "",
        });
      });
    });

    // ---- INVOICE ----
    const invList = Array.isArray(dataObj.invoice_data)
      ? dataObj.invoice_data
      : [];
    invList.forEach((inv) => {
      const items = Array.isArray(inv.line_items) ? inv.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "INVOICE",
          doc_no: inv.invoice_number || "",
          doc_date: inv.invoice_date || "",
          vendor: "",
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.qty != null
              ? li.qty
              : li["qty."] != null
              ? li["qty."]
              : li.received_q != null
              ? li.received_q
              : "",
          uom: li.uom || "",
          unit_price:
            li.unit_price != null
              ? li.unit_price
              : li.rate != null
              ? li.rate
              : "",
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
              ? li.line_total
              : li.parsed_amount != null
              ? li.parsed_amount
              : "",
          po_ref: inv.purchase_order_number || "",
        });
      });
    });

    return rows;
  };

  const toCSV = (dataObj = {}) => {
    const rows = buildRowsFromResponse(dataObj);
    if (rows.length === 0) return "source\n"; // empty CSV with header

    // Preferred header order, plus any extras we discover
    const preferred = [
      "source",
      "doc_no",
      "doc_date",
      "po_ref",
      "vendor",
      "part_no",
      "description",
      "hsn",
      "qty",
      "uom",
      "unit_price",
      "amount",
      "lot_number",
      "expiry_date",
    ];

    // Build union of keys actually present
    const found = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => found.add(k)));

    // Final headers = preferred (if present) + any remaining keys
    const headers = [
      ...preferred.filter((k) => found.has(k)),
      ...[...found].filter((k) => !preferred.includes(k)),
    ];

    // CSV escape
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      // NOTE: we intentionally do not strip commas; we escape them correctly.
    };

    const lines = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => esc(row[h])).join(",")),
    ];

    return lines.join("\n");
  };

  const downloadCSV = (card) => {
    try {
      const csv = toCSV(card?.result_json || {});
      const filename = `${card.match_code || card.twm_code || "twm"}-data.csv`;
      // Add BOM for Excel compatibility
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setSnack({ open: true, type: "error", msg: "Failed to download CSV." });
    }
  };

  const downloadAllCSV = () => {
    try {
      if (!matchedresponse || matchedresponse.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }

      // Build rows from ALL records
      let allRows = [];

      matchedresponse.forEach((card) => {
        const rows = buildRowsFromResponse(card?.result_json || {});
        const enrichedRows = rows.map((row) => ({
          match_code: card.match_code || card.twm_code || "",
          ...row,
        }));
        allRows = allRows.concat(enrichedRows);
      });

      if (allRows.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No line items found." });
        return;
      }

      // Preferred header order
      const headers = [
        "match_code",
        "source",
        "doc_no",
        "doc_date",
        "po_ref",
        "vendor",
        "part_no",
        "description",
        "hsn",
        "qty",
        "uom",
        "unit_price",
        "amount",
        "lot_number",
        "expiry_date",
      ];

      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const csv = [
        headers.join(","),
        ...allRows.map((row) => headers.map((h) => esc(row[h])).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `three_way_matching_all_records.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, type: "error", msg: "Failed to export data." });
    }
  };

  const handleViewItems = (card) => {
    setSelectedCard(card.result_json);
    setViewItemsDialog(true);
  };

  // Helper function to check if current user is an approver and can take action
  const canUserApproveReject = (card) => {
    if (!card.approvers || !user_id) return false;

    // Convert approvers object to array if needed
    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
      ? Object.entries(card.approvers).map(([id, status]) => ({
          id,
          status,
          name: id,
        }))
      : [];

    // Find current user in approvers
    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id
    );

    // User is an approver and hasn't taken action yet (status is PENDING or doesn't exist)
    return currentUserApprover && currentUserApprover.status === "PENDING";
  };

  // Helper function to get current user's approval status
  const getUserApprovalStatus = (card) => {
    if (!card.approvers || !user_id) return null;

    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
      ? Object.entries(card.approvers).map(([id, status]) => ({
          id,
          status,
          name: id,
        }))
      : [];

    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id
    );

    return currentUserApprover ? currentUserApprover.status : null;
  };

  // Helper function to check if current user is an approver for any card
  const isUserAnApprover = () => {
    if (!matchedresponse || !user_id) return false;

    return matchedresponse.some((card) => {
      if (!card.approvers) return false;

      const approversArray = Array.isArray(card.approvers)
        ? card.approvers
        : card.approvers
        ? Object.entries(card.approvers).map(([id, status]) => ({
            id,
            status,
            name: id,
          }))
        : [];

      return approversArray.some(
        (approver) =>
          approver.id === user_id.toString() || approver.id === user_id
      );
    });
  };

  const userIsApprover = isUserAnApprover();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Box>
      {/* MAIN PAGE: search, cards, button (no upload logic/search on upload) */}

      {mode === "main" && (
        <>
          {/* Top bar with search + button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 2,
              p: 2,
            }}
          >
            {![
              "Finance Analyst",
              "Invoice Analyst",
              "Business Finance",
              "Account Payable",
              "Buyer",
            ].includes(user_designation) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={async () => {
                  await fetchNextCode();
                  go("upload");
                }}
                sx={{
                  ml: { xs: 0, sm: 2 },
                  width: { xs: "100%", sm: "auto" },
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: "14px",
                }}
              >
                Create Matching
              </Button>
            )}
          </Box>

          {/* Cards Section */}
          {matchedresponse === null ? (
            // still loading the list
            <Box sx={{ px: 2, py: 4 }}>
              <LinearProgress />
            </Box>
          ) : filterRecords.length === 0 ? (
            // no records OR no records match the current search
            <EmptyState
              title="No records found"
              subtitle={
                [
                  "Finance Analyst",
                  "Invoice Analyst",
                  "Business Finance",
                  "Account Payable",
                  "Buyer",
                ].includes(user_designation)
                  ? "Try clearing the search."
                  : "Try clearing the search, or run a 3-Way Match to create your first record."
              }
            />
          ) : (
            <Grid container p={2}>
              <Grid item xs={12}>
                <Box sx={{ width: "100%", mx: "auto" }}>
                  {/* Section Heading */}
                  <Box sx={{ width: "100%", mb: 1.5 }}>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      color="primary"
                      gutterBottom
                      fontSize={isMobile ? "1.8rem" : "2.6rem"}
                    >
                      Three Way Matching Summary
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontSize: "14px",
                      }}
                    >
                      Review all matched purchase orders, invoices, and GRNs.
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    {/* Search Field */}
                    <TextField
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by code or vendor…"
                      variant="outlined"
                      size="small"
                      sx={{
                        mb: 2,
                        width: { xs: "100%", sm: 320 },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "14px",
                        },
                        "& .MuiInputBase-input": { fontSize: "14px" },
                        "& .MuiSvgIcon-root": { fontSize: "18px" },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchText && (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClear} edge="end">
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadAllCSV}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        fontSize: "14px",
                        mb: 5,
                      }}
                    >
                      Download All
                    </Button>
                  </Box>

                  {/* Unified Paper for Table + Pagination */}
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: "10px",
                      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                      overflow: "hidden", // ensures seamless pagination alignment
                    }}
                  >
                    {/* Table Container */}
                    <TableContainer
                      ref={scrollRef}
                      sx={{
                        maxHeight: "60vh",
                        overflowX: "auto",
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        "&::-webkit-scrollbar": { height: 0, width: 0 },
                      }}
                    >
                      <Table
                        size="small"
                        stickyHeader
                        sx={{
                          minWidth: userIsApprover ? 2100 : 1950, // Increased width for new columns
                          tableLayout: "fixed",
                          borderRight: "1px solid #e2e8f0",
                        }}
                      >
                        {/* ===== Table Head ===== */}
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: "#f8fafc",
                              "& th": {
                                py: 1.2,
                                borderBottom: "2px solid #e2e8f0",
                                fontWeight: 700,
                                fontSize: "13px",
                                color: "#334155",
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                position: "sticky",
                                whiteSpace: "nowrap",
                                left: 0,
                                zIndex: 3,
                                backgroundColor: "#f8fafc",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#f1f5f9" },
                              }}
                              onClick={() => handleSortByInvoiceRef()}
                            >
                              TWM ID {sortOrder === "desc" ? "↓" : "↑"}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Vendor Name
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              PO Number
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              PO Bill Date
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Invoice Number
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Invoice Date
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              GRN Number
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              GRN Bill Date
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              PO Grand Total
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Invoice Grand Total
                            </TableCell>

                            {/* New Status Columns */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Quantity Status
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Price Status
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Item Status
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Matching Date
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              Approval Status
                            </TableCell>

                            {userIsApprover && (
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 120,
                                  width: 150,
                                  minWidth: 150,
                                  zIndex: 3,
                                  backgroundColor: "#f8fafc",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Approve / Reject
                              </TableCell>
                            )}

                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                width: 120,
                                minWidth: 120,
                                zIndex: 4,
                                backgroundColor: "#f8fafc",
                                textAlign: "center",
                                boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        {/* ===== Table Body ===== */}
                        <TableBody>
                          {getCurrentPageData().map((card, idx) => {
                            const po = card.result_json.po_data?.[0];
                            const invoice = card.result_json.invoice_data?.[0];
                            const grn = card.result_json.grn_data?.[0];

                            const poTotalAmt = po?.total_amount
                              ? Number(
                                  po.total_amount.toString().replace(/,/g, "")
                                ).toLocaleString("en-IN")
                              : "—";
                            const invoiceTotalAmt = invoice?.invoice_amount
                              ? Number(
                                  invoice.invoice_amount
                                    .toString()
                                    .replace(/,/g, "")
                                ).toLocaleString("en-IN")
                              : "—";

                            const priceMatch =
                              poTotalAmt === invoiceTotalAmt
                                ? "Matched"
                                : "Mismatch";

                            const quantityMatchStatus = analyzeQuantityMatch(
                              card.result_json.po_data,
                              card.result_json.grn_data,
                              card.result_json.invoice_data
                            );

                            const priceMatchStatus = analyzePriceMatch(
                              card.result_json.po_data,
                              card.result_json.invoice_data
                            );

                            const itemMatchStatus = analyzeItemMatch(
                              card.result_json.po_data,
                              card.result_json.grn_data,
                              card.result_json.invoice_data
                            );

                            const approversArray = Array.isArray(card.approvers)
                              ? card.approvers
                              : card.approvers
                              ? Object.entries(card.approvers).map(
                                  ([id, status]) => ({
                                    id,
                                    status,
                                    name: id,
                                  })
                                )
                              : [];

                            const allApproved =
                              approversArray.length > 0 &&
                              approversArray.every(
                                (a) => a.status === "APPROVED"
                              );
                            const anyRejected = approversArray.some(
                              (a) => a.status === "REJECTED"
                            );
                            const anyPending = approversArray.some(
                              (a) => a.status === "PENDING"
                            );

                            let overallStatus = "Pending";
                            let statusColor = "warning";
                            if (allApproved) {
                              overallStatus = "Approved";
                              statusColor = "success";
                            } else if (anyRejected) {
                              overallStatus = "Rejected";
                              statusColor = "error";
                            }

                            const userCanApproveReject =
                              canUserApproveReject(card);
                            const userApprovalStatus =
                              getUserApprovalStatus(card);

                            return (
                              <TableRow
                                key={idx}
                                sx={{
                                  "& td": {
                                    py: 1.8,
                                    px: 1,
                                    borderBottom: "1px solid #f1f5f9",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: "13px",
                                    color: "#475569",
                                  },
                                }}
                              >
                                {/* Invoice Ref ID */}
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 1,
                                    backgroundColor: "#f8fafc",
                                  }}
                                >
                                  {card.match_code || "—"}
                                </TableCell>

                                {/* Vendor */}
                                <TableCell>
                                  {po?.vendor || grn?.vendor_name || "—"}
                                </TableCell>

                                {/* PO Number */}
                                <TableCell>
                                  {po?.purchase_order_no || "—"}
                                </TableCell>

                                {/* PO Bill Date */}
                                <TableCell>
                                  {formatDate(po?.order_date)}
                                </TableCell>

                                {/* Invoice Number */}
                                <TableCell>
                                  {invoice?.invoice_number || "—"}
                                </TableCell>

                                {/* Invoice Date */}
                                <TableCell>
                                  {formatDate(invoice?.invoice_date)}
                                </TableCell>

                                {/* GRN Number */}
                                <TableCell>
                                  {grn?.order_number || "—"}
                                </TableCell>

                                {/* GRN Bill Date */}
                                <TableCell>
                                  {formatDate(grn?.bill_date)}
                                </TableCell>

                                {/* PO Grand Total */}
                                <TableCell>{poTotalAmt}</TableCell>

                                {/* Invoice Grand Total */}
                                <TableCell>{invoiceTotalAmt}</TableCell>

                                {/* Quantity Status */}
                                <TableCell>
                                  <Tooltip
                                    title="Quantity matching analysis"
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          fontSize: "13px", // 👈 increase font size
                                          fontWeight: 500,
                                        },
                                      },
                                    }}
                                  >
                                    <span>
                                      <StatusChip
                                        status={quantityMatchStatus.status}
                                        type={quantityMatchStatus.type}
                                      />
                                    </span>
                                  </Tooltip>
                                </TableCell>

                                {/* Price Status */}
                                <TableCell>
                                  <Tooltip
                                    title="Price matching analysis"
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          fontSize: "13px", // 👈 increase font size
                                          fontWeight: 500,
                                        },
                                      },
                                    }}
                                  >
                                    <span>
                                      <StatusChip
                                        status={priceMatchStatus.status}
                                        type={priceMatchStatus.type}
                                      />
                                    </span>
                                  </Tooltip>
                                </TableCell>

                                {/* Item Status */}
                                <TableCell>
                                  <Tooltip
                                    title="Item matching analysis"
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          fontSize: "13px", // 👈 increase font size
                                          fontWeight: 500,
                                        },
                                      },
                                    }}
                                  >
                                    <span>
                                      <StatusChip
                                        status={itemMatchStatus.status}
                                        type={itemMatchStatus.type}
                                      />
                                    </span>
                                  </Tooltip>
                                </TableCell>

                                {/* Matching Date */}
                                <TableCell>
                                  {card?.created_at
                                    ? format(
                                        new Date(card.created_at),
                                        "dd MMM yyyy"
                                      )
                                    : "—"}
                                </TableCell>

                                {/* Approval Status */}
                                <TableCell>
                                  {approversArray.length > 0 ? (
                                    <Tooltip
                                      title={
                                        <ApprovalStatusTooltip
                                          approvers={approversArray}
                                        />
                                      }
                                      arrow
                                      placement="top"
                                    >
                                      <Chip
                                        label={overallStatus}
                                        color={statusColor}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          height: "24px",
                                          backgroundColor:
                                            overallStatus === "Approved"
                                              ? "#f0fdf4"
                                              : overallStatus === "Pending"
                                              ? "#fffbeb"
                                              : "#fef2f2",
                                          color:
                                            overallStatus === "Approved"
                                              ? "#166534"
                                              : overallStatus === "Pending"
                                              ? "#d97706"
                                              : "#dc2626",
                                        }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Chip
                                      label="No Status"
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        height: "24px",
                                        backgroundColor: "#f8fafc",
                                        color: "#64748b",
                                      }}
                                    />
                                  )}
                                </TableCell>

                                {/* Approve/Reject Buttons */}
                                {userIsApprover && (
                                  <TableCell
                                    sx={{
                                      position: "sticky",
                                      right: 120,
                                      width: 150,
                                      minWidth: 150,
                                      zIndex: 2,
                                      backgroundColor: "#f8fafc",
                                    }}
                                  >
                                    {userCanApproveReject ? (
                                      <Stack direction="row" spacing={0.5}>
                                        <Button
                                          variant="outlined"
                                          color="success"
                                          size="small"
                                          sx={{
                                            textTransform: "none",
                                            fontWeight: 500,
                                            fontSize: "11px",
                                            px: 1,
                                            height: "32px",
                                          }}
                                          onClick={() =>
                                            approveAndRejectDoc(
                                              "accept",
                                              card.token
                                            )
                                          }
                                        >
                                          Approve
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          sx={{
                                            textTransform: "none",
                                            fontWeight: 500,
                                            fontSize: "11px",
                                            px: 1,
                                            height: "32px",
                                          }}
                                          onClick={() =>
                                            approveAndRejectDoc(
                                              "reject",
                                              card.token
                                            )
                                          }
                                        >
                                          Reject
                                        </Button>
                                      </Stack>
                                    ) : userApprovalStatus ? (
                                      <Chip
                                        label={
                                          userApprovalStatus === "APPROVED"
                                            ? "Approved"
                                            : "Rejected"
                                        }
                                        size="small"
                                        sx={{
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color:
                                            userApprovalStatus === "APPROVED"
                                              ? "#fff"
                                              : "#fff",
                                          backgroundColor:
                                            userApprovalStatus === "APPROVED"
                                              ? "#47a34b"
                                              : "#dc2626",
                                          "& .MuiChip-label": {
                                            px: 1.5,
                                          },
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        fontSize="11px"
                                      >
                                        No action required
                                      </Typography>
                                    )}
                                  </TableCell>
                                )}

                                {/* Actions */}
                                <TableCell
                                  sx={{
                                    position: "sticky",
                                    right: 0,
                                    width: 120,
                                    minWidth: 120,
                                    zIndex: 3,
                                    backgroundColor: "#f8fafc",
                                    boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5,
                                      justifyContent: "space-around",
                                      alignItems: "center",
                                      "& .MuiSvgIcon-root": { fontSize: 20 },
                                    }}
                                  >
                                    <Tooltip title="View Items">
                                      <IconButton
                                        size="large"
                                        onClick={() => handleViewItems(card)}
                                        color="primary"
                                        sx={{ p: 1.25 }}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <DownloadMenuButton
                                      onCSV={() => downloadCSV(card)}
                                      sx={{ minWidth: 0, p: 0.5 }}
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      component="div"
                      count={filterRecords.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        borderTop: "1px solid #e2e8f0",
                        fontSize: "13px",
                        ".MuiTablePagination-toolbar": {
                          minHeight: "52px",
                          fontSize: "14px",
                        },
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                          {
                            fontSize: "14px",
                          },
                        ".MuiTablePagination-select": {
                          fontSize: "14px",
                        },
                        ".MuiTablePagination-actions button": {
                          fontSize: "16px",
                        },
                      }}
                    />
                  </Paper>
                </Box>
              </Grid>

              <TWMOverviewDashboard data={filterRecords} />
            </Grid>
          )}
        </>
      )}

      {/* UPLOAD PAGE: back button, upload panel, NO search field at all */}
      {mode === "upload" && (
        <Box
          sx={{
            minWidth: "100%",
            mx: "auto",
            p: { xs: 2, sm: 3 },
            position: "relative",
          }}
        >
          <>
            {/* Back button at top right */}
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => go("main")}
              sx={{
                position: "absolute",
                top: 0,
                right: 22,
                fontWeight: "700",
              }}
            >
              Back
            </Button>

            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
                mt: 5,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ flexGrow: 1, fontSize: { xs: "14px", sm: "16px" } }}
              >
                Upload PO, GRN & Invoice (PDF or images)
              </Typography>
              {statusChip}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Matching Code
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {nextTwmCode || "—"}
              </Typography>
            </Box>

            {/* Drag & Drop */}
            <Box
              ref={dropRef}
              sx={{
                border: "2px dashed #9bb8f3",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                textAlign: "center",
                background: isDragging ? "#eaf1ff" : "#fafcff",
                transition: "background 160ms ease",
                mb: 3,
              }}
            >
              <UploadFileIcon sx={{ fontSize: 28, color: "#5a6b85" }} />
              <Typography
                variant="body2"
                sx={{ mt: 1, mb: 2, fontSize: { xs: "12px", sm: "14px" } }}
              >
                Drag & drop files here, or select from your computer
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                onClick={(e) => (e.target.value = null)}
                style={{ display: "none" }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<CloudUploadIcon />}
              >
                Browse Files
              </Button>

              {uploadStatus === "uploading" && (
                <Box sx={{ mt: 2, width: "100%" }}>
                  <LinearProgress
                    variant="determinate"
                    value={overallProgress}
                    sx={{ borderRadius: 1 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                    fontSize={12}
                  >
                    {overallProgress || 0}% uploaded
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#5a6b85", fontSize: "14px" }}
                >
                  Selected files
                </Typography>

                <Stack spacing={1.2}>
                  {selectedFiles.map((file) => (
                    <Box
                      key={file.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        borderRadius: 1,
                        background: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: 20, color: "#607d8b" }}
                      />

                      {/* File name + size */}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={file.name}
                          sx={{ fontWeight: 500, fontSize: "13px" }}
                        >
                          {file.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "12px" }}
                        >
                          {formatKB(file.size)}
                        </Typography>
                      </Box>

                      {/* Remove button */}
                      <Tooltip
                        title="Remove"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "10px", // increase tooltip text size
                            },
                          },
                        }}
                      >
                        <span>
                          <IconButton
                            aria-label="Remove file"
                            disabled={uploadStatus === "uploading"}
                            onClick={() => handleRemoveFile(file.name)}
                            size="small"
                          >
                            <ClearIcon color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
              <Dbutton
                label={
                  uploadStatus === "uploading"
                    ? "Uploading..."
                    : "Upload Documents"
                }
                onClick={handleUpload}
                disabled={
                  selectedFiles.length === 0 || uploadStatus === "uploading"
                }
              />
              {uploadStatus === "uploading" && (
                <Dbutton label="Cancel" onClick={handleCancelUpload} />
              )}
              <Dbutton
                label={
                  saving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} color="inherit" />
                      Saving...
                    </Stack>
                  ) : (
                    " Save Result"
                  )
                }
                onClick={handleSaveMatchingWithLoader}
                disabled={
                  selectedFiles.length === 0 ||
                  uploadStatus === "uploading" ||
                  !response ||
                  saving
                }
              />
            </Stack>

            {/* Shimmer / placeholder while parsing */}
            {uploadStatus === "uploading" && !response && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  fontSize={12}
                  color="text.secondary"
                >
                  Parsing documents…
                </Typography>
              </Box>
            )}
          </>

          {/* Results */}
          {response && (
            <Box sx={{ mt: 3 }}>
              <ThreeWayMatchTable
                poData={response.po_data}
                invoiceData={response.invoice_data}
                grnData={response.grn_data}
                showSummary={true}
              />
            </Box>
          )}
        </Box>
      )}

      {mode === "view" && (
        <Box sx={{ p: { xs: 2, sm: 3 }, position: "relative" }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => go("main")}
            sx={{ position: "absolute", top: 0, right: 22, fontWeight: "700" }}
          >
            Back
          </Button>

          <Typography
            variant="subtitle2"
            sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}
          >
            Code:{" "}
            <strong style={{ color: "#1976d2" }}>{savedTwmCode || "—"}</strong>
          </Typography>

          {!viewData ? (
            <Alert severity="info">No saved matching found.</Alert>
          ) : (
            <ThreeWayMatchTable
              poData={viewData.poData}
              invoiceData={viewData.invoiceData}
              grnData={viewData.grnData}
            />
          )}
        </Box>
      )}

      {selectedCard && (
        <ThreeWayMatchTable
          poData={selectedCard.po_data}
          grnData={selectedCard.grn_data}
          invoiceData={selectedCard.invoice_data}
          open={viewItemsDialog}
          onClose={() => setViewItemsDialog(false)}
          showSummary={false} // <-- ADD THIS LINE
        />
      )}

      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Matching Saved!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontSize: "12px" }}>
            Your Three-Way Matching was successfully saved with the code:
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ color: "primary.main", mb: 2 }}
          >
            {savedTwmCode || "—"}
          </Typography>
          <Button
            onClick={() => {
              setShowSuccessDialog(false);
              go("main");
            }}
            variant="contained"
            fullWidth
          >
            OK
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ThreeWayMatching;
